import { type ModalFuncProps } from "antd";
import { type FormikValues } from "formik/dist/types";

import { dispatchTracking } from "utilities/analytics/analytics";

import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { type IDirtyTrackingProps } from "commonComponents/utilities/DirtyTracking/DirtyTracking.utilities";

export interface IUnsavedChangesDialogProps
    extends Omit<
        ModalFuncProps,
        "title" | "content" | "okText" | "okType" | "onOk" | "cancelText" | "onCancel" | "closeable"
    > {
    onCloseWithSave?: () => void | Promise<void | Record<string, string>>;
    onCloseWithoutSave?: () => void;
    /**
     * Runs only when the prompt is closed (its X, or Escape) without choosing either button: the
     * user stays where they are, edits intact.
     */
    onDismiss?: () => void;
    closable?: boolean;
}

/**
 * Prompt the user to save the unsaved changes before exiting a details modal.
 */
export const showUnsavedChangesPrompt = function (
    dirtyKeys: string[],
    props: IUnsavedChangesDialogProps,
) {
    const { onCloseWithSave, onCloseWithoutSave, onDismiss, closable, ...rest } = props;
    // Don't save also ends in onCancel, so its click is recorded to tell it apart from a dismiss.
    let isDontSaveClick = false;
    btConfirm({
        ...rest,
        title: `Unsaved changes`,
        content: <div>Do you want to save your changes before exiting?</div>,
        okText: "Save",
        okType: "primary",
        okButtonProps: { "data-testid": "dirtyTrackingSave" },
        onOk: async () => {
            await onCloseWithSave?.();
        },
        cancelText: "Don't save",
        onCancel: () => {
            if (!isDontSaveClick) {
                onDismiss?.();
            }
        },
        cancelButtonProps: {
            // Need to differentiate between the close and cancel buttons
            // Give onCancel time to fire before closing the parent modal
            onClickCapture: () => {
                isDontSaveClick = true;
                dispatchTracking({
                    event: "ButtonClick",
                    uniqueId: "dirtyTrackingDontSaveData",
                    extraInfo: {
                        dirtyKeys,
                    },
                });
                // eslint-disable-next-line @buildertrend/enterprise-extras/no-unhandled-scheduling
                setTimeout(() => onCloseWithoutSave?.(), 200);
            },
            "data-testid": "dirtyTrackingDontSave",
        },
        closable: closable ?? true,
    });
};

interface IDataLossDialogProps {
    onLeave: () => void;
    onStay: () => void;
}

export const showDataLossPrompt = function (dirtyKeys: string[], props: IDataLossDialogProps) {
    const { onLeave, onStay } = props;
    btConfirm({
        title: "Are you sure you want to leave?",
        content: <div>Your changes will be lost.</div>,
        okText: "Leave",
        okType: "primary",
        okButtonProps: { "data-testid": "dirtyTrackingLeave" },
        onOk: () => {
            dispatchTracking({
                event: "ButtonClick",
                uniqueId: "dirtyTrackingLeaveAndLoseChanges",
                extraInfo: {
                    dirtyKeys,
                },
            });
            onLeave();
        },
        cancelText: "Stay",
        onCancel: onStay,
        cancelButtonProps: { "data-testid": "dirtyTrackingStay" },
        closable: true,
    });
};

export const getUnsavedChangesModalConfig = function <Values extends FormikValues>(
    getDirtyTrackingProps: () => IDirtyTrackingProps<Values>,
    props: IUnsavedChangesDialogProps,
    parentRoute?: string,
    canSave: boolean = true,
    otherDirtyKeys: string[] = [],
    overrideIsDirty?: boolean,
): IModalConfiguration {
    return {
        parentRoute: parentRoute ?? "",
        beforeClose: () => {
            if (!canSave) {
                props.onCloseWithoutSave?.();
            } else {
                const { dirtyKeys, isDirty } = getDirtyTrackingProps();

                if (isDirty || otherDirtyKeys.length > 0 || overrideIsDirty) {
                    showUnsavedChangesPrompt([...dirtyKeys, ...otherDirtyKeys], props);
                } else {
                    props.onCloseWithoutSave?.();
                }
            }
        },
    };
};
